import React, { useEffect, useState, useMemo } from "react";
import "./restaurantCard.css";
import { getHorarioStatus } from "../../../../Utilities/Horarios/horarios";

const SLIDE_INTERVAL = 5000;
const MAX_TAGS = 4;

const RestaurantCard = ({
  data,
  onOpenDetail,
  hasPending = false,       // 👈 nuevo
  checkingPending = false, // 👈 nuevo
}) => {
  const {
    restauranteId,
    name,
    categorias,
    tiempoEntregaEstimado,
    costoEnvio,
    productos,
    _distanceKm,
    isActive,
    createdByUserId,
  } = data || {};

  const [currentSlide, setCurrentSlide] = useState(0);
  const status = getHorarioStatus(data?.horarios || null);

  // Lee el usuario del localStorage (como en MyRestaurants)
  const currentUserId = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("logindata");
      return raw ? JSON.parse(raw)?.data?.userId ?? null : null;
    } catch {
      return null;
    }
  }, []);

  // ¿El dueño es el usuario actual?
  const isOwner = createdByUserId != null && createdByUserId === currentUserId;

  // Permitir click si está abierto O si el dueño es quien intenta abrir
  const canClick = isOwner || status.status !== "closed";

  useEffect(() => {
    if (!Array.isArray(productos) || productos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % productos.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [productos]);

  const activeImage =
    Array.isArray(productos) && productos.length > 0
      ? productos[currentSlide]?.imageUrl
      : null;

  if (!isActive) return null;

  const catArray = Array.isArray(categorias)
    ? categorias.map((c) => String(c).trim()).filter(Boolean)
    : [];
  const cats = catArray.slice(0, MAX_TAGS);
  const extra = Math.max(catArray.length - MAX_TAGS, 0);

  const handleClick = () => {
    if (canClick) onOpenDetail?.(restauranteId);
  };

  const handleKeyDown = (e) => {
    if (!canClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpenDetail?.(restauranteId);
    }
  };

  return (
    <div
      className={`restaurant-card ${canClick ? "clickable" : ""} ${status.status}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={canClick ? "button" : "presentation"}
      tabIndex={canClick ? 0 : -1}
      aria-disabled={!canClick}
    >
      {/* 🔔 Badge de alerta (pendiente/preparando) */}
      {(hasPending && !checkingPending) && (
        <>
          <span className="dotRing" />
          <span className="dot" />
        </>
      )}
      {(checkingPending && !hasPending) && <span className="dotChecking" />}

      {/* Imagen de fondo */}
      <div
        className={`rc-bg ${activeImage ? "" : "rc-bg-fallback"}`}
        style={activeImage ? { backgroundImage: `url(${activeImage})` } : {}}
      >
        <div className="rc-overlay"></div>
        <span className={`rc-badge rc-${status.status}`}>
          {status.label}
        </span>

        {/* Indicador opcional: editable por dueño aunque esté cerrado */}
        {status.status === "closed" && isOwner && (
          <span className="rc-badge rc-owner"></span>
        )}
      </div>

      {/* Contenido sobre la imagen */}
      <div className="rc-content">
        <h3 className="rc-title">{name || "Sin nombre"}</h3>

        <div className="rc-row rc-meta">
          <span>{(_distanceKm ?? 0).toFixed(2)} km</span> ·{" "}
          <span>{tiempoEntregaEstimado ? `${tiempoEntregaEstimado} min` : "—"}</span> ·{" "}
          <span>{costoEnvio != null ? `$${Number(costoEnvio).toFixed(2)} envío` : "Envío —"}</span>
        </div>

        {cats.length > 0 && (
          <div className="rc-tags" aria-label="categorías">
            {cats.map((c, i) => (
              <span key={i} className="rc-tag" title={c}>
                {c}
              </span>
            ))}
            {extra > 0 && (
              <span className="rc-tag rc-tag-more" title={`${extra} más`}>
                +{extra}
              </span>
            )}
          </div>
        )}
      </div>

      {Array.isArray(productos) && productos.length > 1 && (
        <div className="rc-dots">
          {productos.map((_, idx) => (
            <span
              key={idx}
              className={`rc-dot ${idx === currentSlide ? "active" : ""}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantCard;